// @flow

import axios from "axios";
import { type Value } from "@2po-c21/components";
import { type Coordinates } from "@utils/search";

import { LOCALITY_POSTALCODES } from "./helpers";
import settings from "../settings";

const urlEndpoint = "https://api.mapbox.com/geocoding/v5/mapbox.places/";
const mapboxToken = process.env.GATSBY_MAPBOX_TOKEN;
const countryCode = process.env.GATSBY_COUNTRY_CODE;

export const getCustomDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1); // deg2rad below
    const dLon = deg2rad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) *
            Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d;
};

export const getMapBoxUrl = (
    dataType: string,
    types: string,
    limit: string,
    autocomplete: boolean = false,
    latitude?: string,
    longitude?: string,
    language?: string,
) => {
    if (mapboxToken) {
        return `${urlEndpoint}${dataType}.json?access_token=${mapboxToken}${getProximity(
            longitude,
            latitude,
        )}${types ? "&types=" + types : ""}&autocomplete=${
            autocomplete ? "true" : "false"
        }&limit=${limit ? limit : "3"}&fuzzyMatch=false&country=${
            settings.LISTED_COUNTRIES
        }${language ? `&language=${language}` : ""}`;
    }

    throw new Error("Cannot get mapbox token from env");
};

const defaultCoordinates = "4.3547,50.8467";
const getProximity = (longitude, latitude) => {
    if (longitude && latitude) {
        return `&proximity=${longitude},${latitude}`;
    }

    return `&proximity=${defaultCoordinates}`;
};

const deg2rad = (deg) => {
    return deg * (Math.PI / 180);
};

export const findCitiesForAutocomplete = async (
    searchTerm: string,
    language?: string,
): Promise<Array<Value<string>>> => {
    if (!searchTerm) return [];

    const {
        data: { features },
    } = await axios.get(
        getMapBoxUrl(
            searchTerm,
            "postcode%2Clocality%2Cdistrict%2Cplace",
            "10",
            true,
            "",
            "",
            language,
        ),
    );

    return features.map((location) => {
        const countryText = location.context.find((details) =>
            details.id.startsWith("country."),
        );

        const regionText = location.context.find((details) =>
            details.id.startsWith("region."),
        );

        const placeText = location.context.find((details) =>
            details.id.startsWith("place."),
        );

        const localityText =
            LOCALITY_POSTALCODES.includes(searchTerm) &&
            countryText.short_code === "be"
                ? undefined
                : location.context.find((details) =>
                      details.id.startsWith("locality."),
                  );

        const localityValue = localityText ? localityText.text + ", " : "";
        const placeValue = placeText ? placeText.text + ", " : "";
        const regionValue = regionText ? regionText.text + ", " : "";
        const countryValue = countryText ? countryText.text : "";

        const labelText = `${localityValue}${placeValue}${regionValue}${countryValue}`;

        return {
            value: location.center,
            label:
                location.place_type[0] === "postcode"
                    ? labelText
                    : location.place_name,
            text:
                location.place_type[0] === "postcode"
                    ? labelText
                    : location.text,
            coordinates: {
                latitude: location.center[0],
                longitude: location.center[1],
            },
        };
    });
};

export const findPostalCodeForCoordinates = async (
    coordinates: Array<number>,
): Promise<?string> => {
    if (!coordinates) return undefined;
    const {
        data: { features },
    } = await axios.get(getMapBoxUrl(coordinates.join(), "postcode", "1"));
    return features[0].text;
};

export const findPlaceForCoordinates = async (
    coordinates: ?Coordinates,
): Promise<*> => {
    if (!coordinates) return undefined;

    if (coordinates.latitude && coordinates.longitude) {
        const { latitude, longitude } = coordinates;
        const {
            data: { features },
        } = await axios.get(
            getMapBoxUrl(`${latitude},${longitude}`, "place%2Clocality", "1"),
        );

        return features;
    }
};
export const findCoordinatesForPostalCode = async (
    postalCode: string,
): Promise<{ latitude: number, longitude: number }> => {
    const {
        data: { features },
    } = await axios.get(getMapBoxUrl(postalCode, "postcode", "1"));
    const coordinates = features[0].geometry.coordinates;

    return {
        longitude: +coordinates[0],
        latitude: +coordinates[1],
    };
};

export const findCoordinatesForPlace = async (
    place: string,
): Promise<{ latitude: number, longitude: number }> => {
    const {
        data: { features },
    } = await axios.get(getMapBoxUrl(place, "place%2Clocality", "1"));
    const coordinates = features[0].geometry.coordinates;

    return {
        latitude: +coordinates[0],
        longitude: +coordinates[1],
    };
};

/**
 * Finds nearby places with town halls based on a postal code.
 * Uses the Mapbox Search API to:
 * 1. Convert the postal code to coordinates
 * 2. Find town halls near those coordinates
 * 3. Return a unique list of places (first occurrence of each postal code)
 * 4. Filter for unique place names
 * 5. Exclude the input postal code
 * 6. Limit results to specified amount (default 8)
 *
 * @param {string} postalCode - The postal code to search around
 * @param {string} [language] - Optional language code for localized results
 * @param {number} [limit=8] - Maximum number of results to return
 * @returns {Promise<Array<{name: string, postalcode: string}>>} Array of unique places with their postal codes,
 *          ordered by proximity to the input postal code
 * @throws {Error} If the postal code cannot be geocoded or if the Mapbox API request fails
 */
export const findNearbyPlaces = async (
    postalCode: string,
    language?: string,
    limit: number = 8,
): Promise<Array<{ name: string, postalcodes: Array<string> }>> => {
    // Convert postal code to coordinates for proximity search
    const coordinates = await findCoordinatesForPostalCode(postalCode);

    const url = `https://api.mapbox.com/search/searchbox/v1/category/townhall?country=${countryCode}&limit=25&proximity=${
        coordinates.longitude
    },${coordinates.latitude}&access_token=${mapboxToken}${
        language ? `&language=${language}` : ""
    }`;

    const {
        data: { features },
    } = await axios.get(url);

    const seenNames = new Set();
    const placeResults = [];

    features.forEach((feature) => {
        const name = feature.properties.context.place.name;
        const postcode = feature.properties.context.postcode.name;

        if (!seenNames.has(name)) {
            seenNames.add(name);
            placeResults.push({
                name,
                postalcodes: [postcode],
            });
        } else {
            const placeIndex = placeResults.findIndex((p) => p.name === name);
            if (
                placeIndex !== -1 &&
                !placeResults[placeIndex].postalcodes.includes(postcode)
            ) {
                placeResults[placeIndex] = {
                    ...placeResults[placeIndex],
                    postalcodes: [
                        ...placeResults[placeIndex].postalcodes,
                        postcode,
                    ],
                };
            }
        }
    });

    const filteredResults = placeResults.filter(
        (place) => !place.postalcodes.includes(postalCode),
    );

    return filteredResults.slice(0, limit);
};
